/**
 * useSCICustomerCard
 *
 * Single home for the SCI-side callback logic that every consumer of the customer card otherwise
 * repeats — reconciling the card's saved form values back into SCI state, seeding the activity
 * store, and resolving the deal id. `CustomerInformationCard` (the shared details/financing card)
 * calls this with the options for its mode and renders the activity's card directly. It also owns the
 * card's view logic — the ref to the underlying `UnifiedCustomerCard`, the footer-relevant slice of the
 * card's host state, and the apply/cancel handlers — so the component stays pure markup. Kept in
 * `sales-checkin-components` because the activity lib cannot depend on the SCI store/ui-utils
 * without a circular dependency.
 *
 * Consumers: financing card, showroom customer-details slideout, deal setup, etc. Behaviour that
 * differs between them is opt-in via options (onClose, syncSelectedCustomer). Seeding the activity
 * store is owned entirely by the SCI customer-card middleware (INIT_PENCIL_SUCCESS + customer-slice
 * updates), so this hook no longer seeds.
 */
import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type {
    CardOperation,
    CustomerCardHostState,
    CustomerCardSaveData,
    CustomerSubTabKey,
    OnAnalyticsEvent,
    UnifiedCustomerCardRef
} from '@makemydeal/dr-activities-customer-card';
import { CUSTOMER_CARD_ANALYTICS } from '@makemydeal/dr-activities-customer-card';
import type { CommonConsumer } from '@makemydeal/dr-platform-types';
import { mapShopperToConsumer } from '@makemydeal/dr-sales-checkin-ui-utils';
import {
    creditConsentClicked,
    getDealXgId,
    refreshDealRequested,
    setSelectedCustomer,
    updateParticipantDetails,
    useCustomerSelector
} from '@makemydeal/dr-sales-checkin-store';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';

import type { UseSciCustomerCardOptions, UseSciCustomerCardResult } from '../types';
import { useBusinessConversion } from './useBusinessConversion';

export const useSCICustomerCard = ({
    initialTab,
    onClose,
    syncSelectedCustomer = false,
    onSave: hostOnSave,
    onStateChange
}: UseSciCustomerCardOptions = {}): UseSciCustomerCardResult => {
    const dispatch = useDispatch();
    const { checkedInCustomer, coBuyer } = useCustomerSelector();
    const dealId = useSelector(getDealXgId);
    const enableSpd = useSelector(dealerSelectors.isSpdManagementEnabled);
    const enableCorrectPhoneHandling = useSelector(featureToggleSelectors.isCorrectPhoneHandlingEnabled);

    const activeSubTabRef = useRef<CustomerSubTabKey | undefined>(undefined);

    const handleSave = useCallback(
        (data: CustomerCardSaveData) => {
            const subTab = activeSubTabRef.current;
            // update customer slice according to recent customer card changes
            if (data.buyer) {
                const mergedBuyer = {
                    ...checkedInCustomer,
                    ...mapShopperToConsumer(data.buyer, enableCorrectPhoneHandling)
                } as CommonConsumer;
                dispatch(updateParticipantDetails('buyer', mergedBuyer, subTab));
                if (syncSelectedCustomer) dispatch(setSelectedCustomer(mergedBuyer));
            }
            if (data.coBuyer) {
                const mergedCoBuyer = {
                    ...coBuyer,
                    ...mapShopperToConsumer(data.coBuyer, enableCorrectPhoneHandling)
                } as CommonConsumer;
                dispatch(updateParticipantDetails('coBuyer', mergedCoBuyer, subTab));
            }
            onClose?.();
        },
        [dispatch, checkedInCustomer, coBuyer, onClose, syncSelectedCustomer, enableCorrectPhoneHandling]
    );

    // Hosts that drive their own follow-up (deal setup → continue launch) override the default
    // reconcile-only save.
    const onSave = hostOnSave ?? handleSave;

    const cardRef = useRef<UnifiedCustomerCardRef>(null);

    // `canCancel` mirrors dash: the card always reports it true, so Cancel is enabled on mount
    // (independent of `canApply`, which only turns true once the form is dirty & valid).
    const [{ isSaving, canApply, canCancel, operation, canContinue }, setCardState] = useState<{
        isSaving: boolean;
        canApply: boolean;
        canCancel: boolean;
        operation: CardOperation | null;
        canContinue: boolean;
    }>({
        isSaving: false,
        canApply: false,
        canCancel: true,
        operation: null,
        canContinue: false
    });

    // The business-customer UI model — Convert-to-Business signals, view flags, and ref handlers.
    const businessConversion = useBusinessConversion({ cardRef, operation });

    const onCardStateChange = useCallback(
        (state: CustomerCardHostState) => {
            activeSubTabRef.current = state.activeSubTab;
            setCardState({
                isSaving: Boolean(state.isSaving),
                canApply: Boolean(state.canApply),
                canCancel: Boolean(state.canCancel),
                operation: state.operation ?? null,
                canContinue: Boolean(state.canContinue)
            });
            // Forward raw state to a controlling host so it can drive its own action bar.
            onStateChange?.(state);
        },
        [onStateChange]
    );

    const onAnalyticsEvent = useCallback<OnAnalyticsEvent>(
        (eventName, data) => {
            if (eventName === CUSTOMER_CARD_ANALYTICS.CREDIT_CONSENT_CLICKED) {
                dispatch(creditConsentClicked({ checked: Boolean(data?.checked), role: String(data?.role ?? '') }));
            }
        },
        [dispatch]
    );

    const onApply = useCallback(() => cardRef.current?.apply(), []);
    // Delegate to the card's imperative cancel so it runs the discard-changes check before closing
    // (the card invokes its `onCancel` prop — wired to `onClose` — once the user confirms/clean).
    const onCancel = useCallback(() => cardRef.current?.cancel(), []);

    // `activeDeal.details.participants`/`dealStructure.participants.buyer` (what the nav bar's
    // business-buyer detection reads) only update via a full deal fetch — the conversion save
    // itself only touches the activity's own slice and `customer.checkedInCustomer`. Requesting a
    // refresh here reuses the same signal the deal-refresh pipeline dispatches, so the nav bar
    // picks up the Business status/legal name without a manual refresh. `skipGlobalSpinner` keeps
    // this background refresh from painting the full-page loader over the card while it's still
    // closing, unlike the user-facing deal-refresh-modal path which reuses the same signal.
    const onBusinessConversionComplete = useCallback(() => {
        dispatch(refreshDealRequested({ skipGlobalSpinner: true }));
    }, [dispatch]);

    return {
        dealId,
        initialTab,
        onSave,
        enableSpd,
        cardRef,
        onCardStateChange,
        onApply,
        onCancel,
        onAnalyticsEvent,
        onBusinessConversionComplete,
        isSaving,
        canApply,
        canCancel,
        canContinue,
        ...businessConversion
    };
};

export default useSCICustomerCard;
